import {
  Card,
  type CardPadding,
  type CardProps,
  type CardVariant,
  cn,
  Typography,
} from "@norican/react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

export interface SectionCardProps extends Omit<ComponentPropsWithoutRef<"div">, "title"> {
  /** Section heading. Rendered as an `<h2>` so the page keeps one `<h1>`. */
  title?: ReactNode;
  /** Muted line under the heading. */
  description?: ReactNode;
  /** Right-aligned controls in the header row. */
  actions?: ReactNode;
  /** Content for the card footer. Omit for no footer. */
  footer?: ReactNode;
  /** Design system card variant. Defaults to `outline` — calmer for stacked sections. */
  variant?: CardVariant;
  /** Body padding. Defaults to `md`. */
  padding?: CardPadding;
  /** A tinted left edge — a status at a glance (the Usage page's traffic lights). Omit for none. */
  accentTone?: CardProps["accentTone"];
  children: ReactNode;
}

/**
 * A titled content section built on the design system `Card`.
 *
 * Use it for every block of page content — it keeps heading levels, spacing and
 * the header/action layout identical across features, which matters more here
 * than on most apps because three teams are building pages in parallel.
 */
export function SectionCard({
  title,
  description,
  actions,
  footer,
  variant = "outline",
  padding = "md",
  accentTone,
  className,
  children,
  ...props
}: SectionCardProps) {
  const hasHeader = title !== undefined || description !== undefined || actions !== undefined;

  return (
    <Card
      variant={variant}
      className={cn("w-full", className)}
      {...(accentTone === undefined ? {} : { accent: "left" as const, accentTone })}
      {...props}
    >
      {hasHeader ? (
        <Card.Header>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 flex-col gap-1">
              {title === undefined ? null : (
                <Typography as="h2" variant="heading-4">
                  {title}
                </Typography>
              )}
              {description === undefined ? null : (
                <Typography variant="body-sm" tone="muted">
                  {description}
                </Typography>
              )}
            </div>
            {actions === undefined ? null : (
              <div className="flex shrink-0 items-center gap-2">{actions}</div>
            )}
          </div>
        </Card.Header>
      ) : null}
      <Card.Body padding={padding}>{children}</Card.Body>
      {footer === undefined ? null : <Card.Footer>{footer}</Card.Footer>}
    </Card>
  );
}
